import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Search, Users, Briefcase } from "lucide-react";
import { StaffTable } from "./components/StaffTable";
import { OpenPositions } from "./components/OpenPositions";
import { AddPositionModal } from "./components/AddPositionModal";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useTranslation } from 'react-i18next';

export function StaffDashboard() {
  const [activeTab, setActiveTab] = useState("current-staff");
  const [isAddingPosition, setIsAddingPosition] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("all");
  const { t } = useTranslation('business');
  const { t: tc } = useTranslation('common');

  return (
    <div className="min-h-screen bg-gray-50/50 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold text-gray-900">{t('staffManagement')}</h1>
          <Button 
            onClick={() => setIsAddingPosition(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('addNewPosition')}
          </Button>
        </div>

        <Card className="border-none shadow-sm">
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="border-b border-slate-200/60 px-6">
                <TabsList className="bg-slate-50/80 p-1 rounded-lg shadow-sm border border-slate-200/60 h-12">
                  <TabsTrigger 
                    value="current-staff"
                    className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
                  >
                    <Users className="w-4 h-4" />
                    {t('currentStaff')}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="open-positions"
                    className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
                  >
                    <Briefcase className="w-4 h-4" />
                    {t('openPositions')}
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="p-6">
                <div className="flex gap-4 items-center mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
                    <Input
                      placeholder={tc('search') + "..."}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={filterBy} onValueChange={setFilterBy}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder={t('filterBy')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('allStaff')}</SelectItem>
                      <SelectItem value="full-time">{t('fullTime')}</SelectItem>
                      <SelectItem value="part-time">{t('partTime')}</SelectItem>
                      <SelectItem value="contract">{t('contract')}</SelectItem>
                      <SelectItem value="volunteer">{t('volunteer')}</SelectItem>
                      <SelectItem value="intern">{t('intern')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <TabsContent value="current-staff" className="mt-0">
                  <div className="h-[600px] overflow-y-auto">
                    <StaffTable searchTerm={searchTerm} />
                  </div>
                </TabsContent>

                <TabsContent value="open-positions" className="mt-0">
                  <div className="h-[600px] overflow-y-auto">
                    <OpenPositions />
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        <AddPositionModal 
          isOpen={isAddingPosition}
          onClose={() => setIsAddingPosition(false)}
        />
      </div>
    </div>
  );
}